import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Stack, Typography, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import PrintIcon from '@mui/icons-material/Print';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const HTMLViewer = ({ htmlUrl }) => {
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    // Reset state when a new HTML file is loaded
    setZoom(1);
    setError(null);
    setIsLoaded(false);
  }, [htmlUrl]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));
  const handleResetZoom = () => setZoom(1);

  const handlePrint = () => {
    if (iframeRef.current) {
      try {
        // Try to access iframe content for printing
        const iframeWindow = iframeRef.current.contentWindow;
        if (iframeWindow) {
          iframeWindow.focus();
          iframeWindow.print();
        } else {
          // Fallback: Open in new tab for printing
          window.open(htmlUrl, '_blank');
        }
      } catch (e) {
        console.error('Error accessing iframe content for printing:', e);
        // Fallback: Open in new tab for printing
        window.open(htmlUrl, '_blank');
      }
    }
  };

  const handleOpenInNewTab = () => {
    window.open(htmlUrl, '_blank');
  };

  const handleIframeLoad = () => {
    setIsLoaded(true);
  };

  const handleIframeError = () => {
    setError('Error loading HTML content. The file may be invalid or contain unsupported elements.');
    setIsLoaded(true); // Set to true to remove loading indicator
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <IconButton onClick={handleZoomOut} title="Zoom Out">
          <ZoomOutIcon />
        </IconButton>
        <IconButton onClick={handleResetZoom} title="Reset Zoom">
          <FitScreenIcon />
        </IconButton>
        <IconButton onClick={handleZoomIn} title="Zoom In">
          <ZoomInIcon />
        </IconButton>
        <Button 
          variant="contained" 
          startIcon={<PrintIcon />} 
          onClick={handlePrint}
          size="small"
        >
          Print
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<OpenInNewIcon />} 
          onClick={handleOpenInNewTab}
          size="small"
        >
          Open in New Tab
        </Button>
      </Stack>

      <Box
        sx={{
          border: '1px solid #ccc',
          height: '500px',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
        }}
      >
        {!isLoaded && !error && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 1
            }}
          >
            Loading HTML content...
          </Box>
        )}
        
        {error ? (
          <Box 
            sx={{ 
              color: 'error.main', 
              textAlign: 'center', 
              p: 2,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%'
            }}
          >
            <Typography variant="h6" color="error" gutterBottom>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleOpenInNewTab}
              sx={{ mt: 2 }}
            >
              Open in New Tab
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              overflow: 'auto',
            }}
          >
            <iframe
              ref={iframeRef}
              src={htmlUrl}
              title="HTML Content Viewer"
              width="100%"
              height="100%"
              style={{ border: 'none', display: isLoaded ? 'block' : 'none' }}
              sandbox="allow-same-origin allow-scripts allow-forms"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          </Box>
        )}
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Note: Some HTML content may require additional resources or permissions to display correctly.
        If you encounter issues, try using the "Open in New Tab" button.
      </Typography>
    </Box>
  );
};

export default HTMLViewer;